import { FC, useEffect, useMemo, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';

import { ResourceTypeExplorer } from '@dbt-labs/dbt-dag';
import { TrustSignals } from '@dbt-labs/metadata-shared';
import { Button, PaginationFooter, Pill, Sizes } from '@dbt-labs/sourdough';

import { DbtAsset, SearchFieldType, WarehouseAsset } from '@/__generated__/graphql';
import { PageHeading } from '@/components/PageHeading';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { FilterState, NodeFilters, useFilterContext } from '@/context/FilterProvider';
import { pluralizeResourceType } from '@/util/resourceType';
import { toTitleCase } from '@/util/string';
import { AccountSearchResultItem } from './AccountSearchResultItem';

const DEFAULT_MAX_PILL_COUNT = 9;

export const getResultCountString = (count: number | undefined) => {
  if (count === undefined) return null;
  if (count === 1) return '1 result';
  return `${count} results`;
};

export type AccountSearchResultHit = {
  dbtAsset: DbtAsset | null;
  warehouseAsset: WarehouseAsset | null;
  matchedField: SearchFieldType;
  highlight: string | null;
  highlights?: Partial<Record<SearchFieldType, string[]>>;
  resultName: string;
  numCols: number | null;
};

export type AccountSearchResultDisplayData = {
  hit: AccountSearchResultHit;
  tags?: never;
  columns?: never;
};

// TODO: make hit.dbtAsset properly work with this function
export function getTrustSignalsFromHit(hit: AccountSearchResultHit): TrustSignals {
  switch (hit.dbtAsset?.resourceType) {
    case 'model':
      return {
        healthIssues: (hit.dbtAsset as any).modelHealthIssues ?? [],
      };
    case 'source':
      return {
        healthIssues: (hit.dbtAsset as any).sourceHealthIssues ?? [],
        additionalMetadata: {
          freshnessChecked: (hit.dbtAsset as any).freshness?.freshnessChecked,
        },
      };
    case 'exposure':
      return { healthIssues: (hit.dbtAsset as any).exposureHealthIssues ?? [] };
    default:
      return { healthIssues: [] };
  }
}

export interface AccountSearchResultsParams {
  isError: boolean;
  pageSize: number;
  query: string;
  title: string;
  isLoadingMore: boolean;
  skeleton?: boolean;
  hasMoreResults: boolean;
  data: AccountSearchResultDisplayData[] | undefined;
  totalCount: number | undefined;
  fetchMore: () => void;
  testId?: string;
}

export const AccountSearchResultsList: FC<
  Omit<AccountSearchResultsParams, 'title' | 'error' | 'totalCount'>
> = ({
  query,
  testId = 'search-result',
  isLoadingMore,
  skeleton,
  hasMoreResults,
  pageSize,
  data,
  fetchMore,
}) => {
  const displayedData = useMemo(() => {
    let displayedData: (AccountSearchResultDisplayData | 'skeleton')[] | undefined =
      data ? [...data] : undefined;
    if (skeleton || isLoadingMore) {
      displayedData ??= new Array<'skeleton'>();
      const skeletonElements = new Array<'skeleton'>(pageSize).fill('skeleton');
      displayedData.push(...skeletonElements);
    }

    return displayedData;
  }, [data, isLoadingMore, pageSize, skeleton]);

  return (
    <div className="space-y-2">
      {displayedData?.map((searchResult, index) => {
        if (searchResult === 'skeleton') {
          return <AccountSearchResultItem key={`${skeleton}-${index}`} skeleton />;
        }
        return (
          <AccountSearchResultItem
            key={index}
            query={query}
            data={searchResult}
            testId={`${testId}-${index}`}
            resultIndex={index}
          />
        );
      })}
      {displayedData?.length === 0 && <div>No results were found</div>}
      {hasMoreResults && (
        <PaginationFooter
          hasMorePages={hasMoreResults}
          onLoadMore={fetchMore}
          isPageLoading={isLoadingMore || skeleton}
        />
      )}
    </div>
  );
};

const NoSearchResults: FC<AccountSearchResultsParams> = (props) => {
  useEffect(() => {
    datadogRum.addAction('explorer-no-search-results');
  }, []);
  return (
    <div className="my-8">
      <h1 className="mx-auto mb-2.5 w-fit text-4xl font-medium text-fgMain">
        No search results for "{props.query || props.title}"
      </h1>
      <div className="mx-auto w-fit text-fgDecorative">
        We couldn't find exactly what you were looking for.
      </div>
    </div>
  );
};

const formatBadgeText = (filterName: keyof NodeFilters, item: string | number) => {
  if (filterName === 'resourceType')
    return pluralizeResourceType(item as ResourceTypeExplorer, true);
  if (item === 'dbt') return 'dbt';
  return toTitleCase(item.toString());
};

const getFilterConfig = (filterName: keyof NodeFilters) => {
  const configs: Record<
    string,
    {
      renderPill: (
        filterCtx: FilterState,
        getProjectName: (id: number) => string | undefined,
      ) => React.ReactNode;
      shouldRender: (filterCtx: FilterState) => boolean;
    }
  > = {
    column: {
      shouldRender: (filterCtx) => !!filterCtx.filters.column,
      renderPill: (filterCtx) => (
        <Pill
          id="column"
          key="column"
          value={pluralizeResourceType('column', true)}
          onClickRemove={() => filterCtx.updateFilters({ column: false }, false)}
        />
      ),
    },
    projects: {
      shouldRender: (filterCtx) => !!filterCtx.filters.projects,
      renderPill: (filterCtx, getProjectName) => {
        const projects = filterCtx.filters.projects;
        if (!Array.isArray(projects)) return null;

        return projects.map((item: number) => (
          <Pill
            id="projects"
            key={`projects-${item}`}
            value={getProjectName(item) || item.toString()}
            onClickRemove={() => filterCtx.updateFilters({ projects: [item] }, false)}
          />
        ));
      },
    },
    default: {
      shouldRender: (filterCtx) => !!filterCtx.filters[filterName],
      renderPill: (filterCtx) => {
        const filterValue = filterCtx.filters[filterName];
        if (!Array.isArray(filterValue)) return null;

        return filterValue.map((item: string | number) => (
          <Pill
            id={filterName}
            key={`${filterName}-${item}`}
            value={formatBadgeText(filterName, item)}
            onClickRemove={() =>
              filterCtx.updateFilters({ [filterName]: [item] }, false)
            }
          />
        ));
      },
    },
  };

  return configs[filterName] || configs.default;
};

const SearchFilterPills: FC = () => {
  const filterCtx = useFilterContext();
  const { getProjectName } = useDbtCloudAccount();
  const [isExpanded, setIsExpanded] = useState(false);

  if (filterCtx.activeFilterCount === 0) {
    return null;
  }

  const filterTypes = [
    'resourceType',
    'column',
    'access',
    'tags',
    'materialization',
    'modelingLayer',
    'searchFields',
    'platforms',
    'projects',
    'environmentTypes',
  ] satisfies (keyof NodeFilters)[];

  const allPills: React.ReactNode[] = [];

  filterTypes.forEach((filterName) => {
    const config = getFilterConfig(filterName);
    if (config.shouldRender(filterCtx)) {
      const pills = config.renderPill(filterCtx, getProjectName);
      if (Array.isArray(pills)) {
        allPills.push(...pills);
      } else if (pills) {
        allPills.push(pills);
      }
    }
  });

  const shouldShowExpandButton = allPills.length > DEFAULT_MAX_PILL_COUNT;
  const pillsToShow = isExpanded ? allPills : allPills.slice(0, DEFAULT_MAX_PILL_COUNT);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {pillsToShow}
      {shouldShowExpandButton && (
        <Button
          type="tertiary"
          size={Sizes.sm}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Show less' : `Show all ${allPills.length}`}
        </Button>
      )}
      <Button type="tertiary" size={Sizes.sm} onClick={filterCtx.clearFilters}>
        Clear all
      </Button>
    </div>
  );
};

export const AccountSearchResults: FC<AccountSearchResultsParams> = (props) => {
  const { accountId } = useDbtCloudAccount();

  if (props?.isError || props?.data?.length === 0) {
    return <NoSearchResults {...props} />;
  }

  if (!accountId) {
    return null;
  }

  const resultCount = props?.data?.length;
  return (
    <>
      <PageHeading
        additional={{
          right: resultCount !== undefined && (
            <p className="whitespace-nowrap text-right text-fgAlt">
              {getResultCountString(props.totalCount)}
            </p>
          ),
        }}
        className="mb-2 flex flex-row items-center justify-between gap-2"
      >
        {props.query ? `Results for "${props.query}"` : 'All results'}
      </PageHeading>
      {(resultCount === undefined || resultCount > 0) && (
        <div className="mb-4 space-y-4 text-fgDecorative">
          <SearchFilterPills />
        </div>
      )}
      <div>
        <AccountSearchResultsList {...props} />
      </div>
    </>
  );
};
